import React, {useEffect, useRef, useState} from 'react'
import {OnDashPageTitle} from '../cores/OnDashPageData'
import {collection, onSnapshot, doc, setDoc, serverTimestamp} from '@firebase/firestore'
import db from '../../../services/firebase'
import {useAuth} from '../../modules/auth'
import {Helmet} from 'react-helmet'
import {useNavigate} from 'react-router-dom'
import {renderAsync} from 'docx-preview'
import {format} from 'date-fns'
import { showSuccessToast } from 'app/components/ShowToast'
import { ToastContainer } from 'react-toastify'
import { CustomFile } from '../Utils/ontypes'

let WebViewer: any = null
if (typeof window !== 'undefined') {
  import('@pdftron/pdfjs-express-viewer').then((module) => {
    WebViewer = module.default
  })
}

interface WebViewerInstance {
  dispose: () => void
  Core: any
  UI: any
}

interface WebViewerOptions {
  path: string
  initialDoc: string
  enableAnnotations?: boolean
  licenseKey?: string
  disabledElements?: string[]
  customCss?: string
  initialZoom?: number
  initialViewMode?: string
  pageTransition?: string
}

const Policies: React.FC = () => {
  const [policies, setPolicies] = useState<CustomFile[]>([])
  const [selectedFile, setSelectedFile] = useState<CustomFile | null>(null)
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null)
  const {currentUser} = useAuth()
  const [lastPage, setLastPage] = useState(false)
  const [policyData, setPolicyData] = useState<any>([])
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const [personnelPolicies, setPersonnelPolicies] = useState('')
  const [filteredPolicies, setFilteredPolicies] = useState<CustomFile[]>([])
  const [fileType, setFileType] = useState<'pdf' | 'docx' | null>(null)
  const [reachedEnd, setReachedEnd] = useState(false)
  const docxContainerRef = useRef<HTMLDivElement>(null)
  const [isDocumentLoading, setIsDocumentLoading] = useState(true)
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const viewerRef = useRef<HTMLDivElement>(null)
  const webViewerInstance = useRef<WebViewerInstance | null>(null)

  useEffect(() => {
    if (selectedFile) {
      setIsDocumentLoading(true)
    }
  }, [selectedFile])

  const renderDocx = async (url: string) => {
    try {
      setIsDocumentLoading(true)

      if (docxContainerRef.current) {
        docxContainerRef.current.innerHTML = ''

        const innerContainer = document.createElement('div')
        innerContainer.style.width = '100%'
        innerContainer.style.padding = ''
        innerContainer.style.backgroundColor = '#fff'
        innerContainer.style.boxShadow = '0 0 10px rgba(0,0,0,0.1)'
        innerContainer.style.borderRadius = '4px'
        docxContainerRef.current.appendChild(innerContainer)

        const response = await fetch(url)
        const blob = await response.blob()

        await renderAsync(blob, innerContainer, innerContainer, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          debug: false,
        })
      }
    } catch (error) {
      console.error('Error rendering DOCX:', error)
      setFileType(null)
    } finally {
      setIsDocumentLoading(false)
      scrollDocxToTop()
    }
  }

  useEffect(() => {
    if (filteredPolicies.length > 0) {
      setSelectedFile(filteredPolicies[0])
      setSelectedFileIndex(0)
    }
  }, [policies])

  useEffect(() => {
    scrollDocxToTop()
  }, [selectedFile])

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    if (!tenantId) {
      return
    }
    const tenantRef = doc(db, 'tenants', tenantId)
    const PolicyRef = collection(tenantRef, 'policies')

    const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
      const policyData = snapshot.docs.map((doc) => ({
        ...(doc.data() as CustomFile),
        id: doc.id,
        policy: doc.data().policy,
      }))

      const filtered = policyData.filter((policy) =>
        personnelPolicies ? personnelPolicies.includes(policy.policy) : false
      )

      setFilteredPolicies(filtered)
      setPolicies(policyData)
    })

    return unsubscribe
  }, [currentUser, personnelPolicies])

  useEffect(() => {
    if (selectedFile && selectedFile.customfile) {
      const sortedFiles = selectedFile.customfile.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      const fileExtension = sortedFiles[0].name.split('.').pop()?.toLowerCase()

      if (fileExtension === 'pdf') {
        setFileType('pdf')
      } else if (fileExtension === 'docx' || fileExtension === 'doc') {
        setFileType('docx')
      } else {
        setFileType(null)
      }
    }
  }, [selectedFile])

  useEffect(() => {
    if (selectedFile && fileType === 'docx' && selectedFile.customfile) {
      const sortedFiles = selectedFile.customfile.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      renderDocx(sortedFiles[0].url)
    }
  }, [selectedFile, fileType])

  // Initialize WebViewer when PDF file is selected
  useEffect(() => {
    if (selectedFile && fileType === 'pdf' && selectedFile.customfile && !webViewerInstance.current) {
      const sortedFiles = selectedFile.customfile.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
      const pdfUrl = sortedFiles[0].url

      const initWebViewer = async () => {
        try {
          setIsDocumentLoading(true)
          setError(null)

          if (!WebViewer) {
            await new Promise((resolve) => {
              const checkWebViewer = () => {
                if (WebViewer) {
                  resolve(true)
                } else {
                  setTimeout(checkWebViewer, 100)
                }
              }
              checkWebViewer()
            })
          }

          if (viewerRef.current) {
            viewerRef.current.innerHTML = ''
          }

          const options: WebViewerOptions = {
            path: '/webviewer/lib',
            initialDoc: pdfUrl,
            enableAnnotations: false,
            licenseKey: process.env.REACT_APP_PDFJS_EXPRESS_VIEWER_LICENSE,
            disabledElements: ['toolsButton', 'menuButton', 'watermark'],
            initialViewMode: 'Facing',
            pageTransition: 'PageCurl',
          }

          const instance = (await WebViewer(options, viewerRef.current)) as WebViewerInstance

          webViewerInstance.current = instance

          // Set up page change listener
          if (instance.Core && instance.Core.documentViewer) {
            instance.Core.documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
              const totalPages = instance.Core.documentViewer.getPageCount()
              setLastPage(pageNumber === totalPages)
            })
          }

          setIsDocumentLoading(false)
        } catch (err) {
          console.error('Error initializing WebViewer:', err)
          setError('Failed to load PDF document')
          setIsDocumentLoading(false)
        }
      }

      const timer = setTimeout(() => {
        initWebViewer()
      }, 200)

      return () => {
        clearTimeout(timer)
      }
    }

    return () => {
      if (webViewerInstance.current) {
        try {
          webViewerInstance.current.dispose()
        } catch (err) {
          console.error('Error disposing WebViewer:', err)
        }
        webViewerInstance.current = null
      }
    }
  }, [selectedFile, fileType])

  useEffect(() => {
    if (fileType === 'docx' && docxContainerRef.current) {
      const container = docxContainerRef.current
      const handleScroll = () => {
        if (container.scrollHeight - container.scrollTop <= container.clientHeight + 50) {
          setReachedEnd(true)
        } else {
          setReachedEnd(false)
        }
      }

      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [fileType])

  const scrollDocxToTop = () => {
    if (docxContainerRef.current) {
      docxContainerRef.current.scrollTop = 0
    }
  }

  const isAcceptButtonDisabled = () => {
    if (selectedFile && policyData[selectedFile?.id]?.isChecked) {
      return true
    }
    if (fileType === 'pdf') {
      return !lastPage
    }
    if (fileType === 'docx') {
      return !reachedEnd
    }
    return !hasDownloaded
  }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!tenantId) {
        return
      }

      const tenantRef = doc(db, 'tenants', tenantId)
      const PolicyRef = collection(tenantRef, 'employees')

      const unsubscribe = onSnapshot(PolicyRef, (snapshot) => {
        const employeesData = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
            fullName: doc.data().full_name,
            uid: doc.data().uid,
            group: doc.data().group,
          }))
          .filter((data) => data.uid === currentUser?.uid)

        if (employeesData.length > 0) {
          const group = employeesData[0].group
          const groupRef = collection(tenantRef, 'personnel_group')

          const unsubscribe = onSnapshot(groupRef, (snapshot) => {
            const groupData = snapshot.docs
              .map((doc) => ({
                ...doc.data(),
                id: doc.id,
                checklist: doc.data().checklist,
                groupName: doc.data().groupName,
              }))
              .filter((data) => data.groupName === group)

            const checklist = groupData[0].checklist
            const checklistRef = collection(tenantRef, 'personnel_checklist')

            const unsubscribe2 = onSnapshot(checklistRef, (snapshot) => {
              const checklistData = snapshot.docs
                .map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                  checklist: doc.data().checklist_name,
                  customTasks: doc.data().CustomTasks || [],
                  policies: doc.data().policies,
                }))
                .filter((data) => data.checklist === checklist)
              const policies = checklistData.length > 0 ? checklistData[0]?.policies || [] : []

              setPersonnelPolicies(policies)
            })
          })
        }
      })
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  useEffect(() => {
    const fetchAllPoliciesData = async () => {
      try {
        const tenantId = currentUser?.tenantId || ''
        const userId = currentUser?.uid || ''
        if (!tenantId || !userId) {
          return
        }

        const policyDataMap = {}
        const unsubscribeArray: (() => void)[] = []

        filteredPolicies.forEach((item) => {
          const policyDocRef = doc(
            db,
            'tenants',
            tenantId,
            'onboarding',
            userId,
            'policies',
            item.id
          )

          const unsubscribe = onSnapshot(policyDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
              policyDataMap[item.id] = docSnapshot.data()
            } else {
              policyDataMap[item.id] = {}
            }

            setPolicyData((prevData) => ({
              ...prevData,
              ...policyDataMap,
            }))
          })

          unsubscribeArray.push(unsubscribe)
        })

        return () => {
          unsubscribeArray.forEach((unsubscribe) => unsubscribe())
        }
      } catch (error) {
        console.error('Error fetching policies data:', error)
      }
    }

    if (filteredPolicies.length > 0) {
      fetchAllPoliciesData()
    }
  }, [filteredPolicies, currentUser])

  const handleFileChange = (file: CustomFile, index: number) => {
    setSelectedFile(file)
    setSelectedFileIndex(index)
    setLastPage(false)
    setHasDownloaded(false) 
    setReachedEnd(false)
    if (docxContainerRef.current) {
      docxContainerRef.current.innerHTML = ''
    }
    scrollDocxToTop()
    
    // Dispose of previous WebViewer instance
    if (webViewerInstance.current) {
      try {
        webViewerInstance.current.dispose()
      } catch (err) {
        console.error('Error disposing WebViewer:', err)
      }
      webViewerInstance.current = null
    }
  }

  const handleAcceptClick = async () => {
    if (selectedFileIndex !== null) {
      const updatedFileList = [...filteredPolicies]
      const selectedPolicy = updatedFileList[selectedFileIndex]
      updatedFileList[selectedFileIndex] = {
        ...selectedPolicy,
        isChecked: true,
        finished: true,
      }

      const {id, policy} = selectedPolicy
      const tenantId = currentUser?.tenantId || ''
      const userId = currentUser?.uid || ''

      if (!tenantId || !userId) {
        setIsLoading(false)
        return
      }

      const policyDocId = selectedFileIndex.toString()
      const policyDocRef = doc(db, 'tenants', tenantId, 'onboarding', userId, 'policies', id)

      try {
        setIsLoading(true)
        const updatedPolicyData = {
          isChecked: true,
          finished: true,
          completedByUid: currentUser?.uid,
          completedBy: currentUser?.userName,
          policy,
          completed_at: serverTimestamp(),
        }

        await setDoc(policyDocRef, updatedPolicyData)

        setPolicyData((prevData) => ({
          ...prevData,
          [policyDocId]: updatedPolicyData,
        }))
        setFilteredPolicies(updatedFileList)

        setIsLoading(false)
        showSuccessToast({message:'Policy accepted successfully'})
        const areAllPoliciesChecked = updatedFileList.every((policy) => policy.isChecked)

        if (areAllPoliciesChecked) {
          setTimeout(() => {
            navigate('/training')
          }, 1000)
        } else {
          const nextFileIndex = selectedFileIndex + 1
          if (nextFileIndex < updatedFileList.length && !updatedFileList[nextFileIndex].isChecked) {
            setSelectedFile(updatedFileList[nextFileIndex])
            setSelectedFileIndex(nextFileIndex)
            setLastPage(false)
            scrollDocxToTop()
            setReachedEnd(false)
          }
        }
      } catch (error) {
        console.error('Error updating policy in Firestore:', error)
        setIsLoading(false)
      }
    }
  }

  return (
    <>
      <Helmet>
        <title>SecureSlate | Policies</title>
      </Helmet>
      <OnDashPageTitle
        icon='policies1.svg'
        title='Policies'
        desc="Read and accept your company's policies."
      />
      <div className='card'>
        <div className='row '>
          <div className='col-md-12'>
            <div className='row m-0 '>
              <div className='col-md-3 border-end overflow-auto' style={{maxHeight: '600px'}}>
                <h2 className='text-dark  mt-3 ml-4 px-4 pt-4 pb-3'>Policies</h2>

                <hr />
                {filteredPolicies
                .sort((a, b) => a.policy.localeCompare(b.policy))
                .map((item, index) => (
                  <div
                    className={`d-flex align-items-center mb-2 p-3  ${
                      selectedFileIndex === index ? 'bg-gray-100 cursor-pointer' : ''
                    }`}
                    key={item.id}
                    onClick={() => handleFileChange(item, index)}
                    style={{borderRadius: '8px'}}
                  >
                      <div className="d-flex align-items-center" style={{ minWidth: '24px', marginRight: '2px' }}>
                      {policyData[item.id]?.isChecked || item.isChecked ? 
                        <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="20"  viewBox="0 0 24 24"  fill="#0e9f6e"  className="icon icon-tabler icons-tabler-filled icon-tabler-circle-check"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 3.34a10 10 0 1 1 -14.995 8.984l-.005 -.324l.005 -.324a10 10 0 0 1 14.995 -8.336zm-1.293 5.953a1 1 0 0 0 -1.32 -.083l-.094 .083l-3.293 3.292l-1.293 -1.292l-.094 -.083a1 1 0 0 0 -1.403 1.403l.083 .094l2 2l.094 .083a1 1 0 0 0 1.226 0l.094 -.083l4 -4l.083 -.094a1 1 0 0 0 -.083 -1.32z" /></svg>
                      :
                      <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="20"  viewBox="0 0 24 24"  fill="none"  stroke="#6a7282"  stroke-width="2"  stroke-linecap="round"  stroke-linejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-circle-dashed"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M8.56 3.69a9 9 0 0 0 -2.92 1.95" /><path d="M3.69 8.56a9 9 0 0 0 -.69 3.44" /><path d="M3.69 15.44a9 9 0 0 0 1.95 2.92" /><path d="M8.56 20.31a9 9 0 0 0 3.44 .69" /><path d="M15.44 20.31a9 9 0 0 0 2.92 -1.95" /><path d="M20.31 15.44a9 9 0 0 0 .69 -3.44" /><path d="M20.31 8.56a9 9 0 0 0 -1.95 -2.92" /><path d="M15.44 3.69a9 9 0 0 0 -3.44 -.69" /></svg>
                      }
                      </div>
                    <div className='ms-2 cursor-pointer'>
                      <div className='text-dark fw-medium text-hover-primary fs-6'>
                        {item.policy}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Right Column (Pdf/docx viewer) */}
              <div className='col-md-9 px-0'>
                <div className='mb-0'>
                  <div className='mb-0'>
                    <div className='overlay d-flex justify-content-center'>
                      {selectedFile ? (
                        <div className='container-fluid'>
                          <div className='row d-flex justify-content-center'>
                            <div className='col col-12 p-0' style={{height: 600}}>
                              {isDocumentLoading && (fileType === 'docx' || fileType === 'pdf') && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 1000,
                                  }}
                                >
                                  <div className='d-flex flex-column align-items-center'>
                                    <div className='spinner-border text-primary mb-3' role='status'>
                                      <span className='visually-hidden'>Loading...</span>
                                    </div>
                                    <div className='fs-6'>Loading document...</div>
                                  </div>
                                </div>
                              )}
                              {fileType === 'pdf' ? (
                                <div
                                  ref={viewerRef}
                                  style={{
                                    height: '100%',
                                    width: '100%',
                                    minHeight: '400px',
                                    backgroundColor: '#f5f5f5',
                                  }}
                                  id='pdf-viewer-container'
                                />
                              ) : fileType === 'docx' ? (
                                <div
                                  ref={docxContainerRef}
                                  id='docx-container'
                                  style={{
                                    height: '100%',
                                    overflow: 'auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%',
                                    margin: '0 auto',
                                    padding: '0',
                                    backgroundColor: '#fff',
                                  }}
                                />
                              ) : (
                                <div
                                style={{
                                  height: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  backgroundColor: '#fff',
                                  flexDirection: 'column',
                                  gap: '1rem',
                                }}
                              >
                                <div className='text-dark fs-4'>File Preview Not Available</div>
                                <div className='text-muted mb-4'>
                                  This file type cannot be previewed in the browser.
                                </div>
                                {selectedFile?.customfile?.length > 0 ? (
                                  <a 
                                    href={selectedFile.customfile[0].url} 
                                    download={selectedFile.customfile[0].name}
                                    style={{backgroundColor: '#0E9F6E', color: '#fff'}}
                                    className='btn btn-sm btn-primary'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    onClick={() => {
                                      setHasDownloaded(true)
                                    }}
                                  >
                                    <i className='fas fa-download me-2'></i>
                                    Download File
                                  </a>
                                ) : (
                                  <div className='text-danger'>
                                    No file available for download
                                  </div>
                                )}
                              </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className='container-fluid'>
                            <div className='row'>
                              <div className='col ' style={{height: 600}}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                  }}
                                >
                                  <div className='mb-2 mx-10 fs-6'>
                                    No checklist policies have been assigned to this group. Please
                                    ask the admin or owner to update the checklist to complete the
                                    onboarding process for the employees in this group.
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className='border p-4 ml-3 '>
                  {selectedFile && policyData[selectedFile?.id]?.isChecked ? (
                    <button
                      type='button'
                      className='btn me-3 '
                      disabled
                      style={{backgroundColor: '#0E9F6E', color: '#fff'}}
                    >
                      Accepted
                    </button>
                  ) : (
                    <button
                      type='button'
                      className='btn me-3 '
                      onClick={handleAcceptClick}
                      style={{backgroundColor: '#0E9F6E', color: '#fff'}}
                      disabled={isAcceptButtonDisabled()}
                    >
                      {isLoading ? (
                        <>
                          <span className='indicator-progress' style={{display: 'block'}}>
                            Please wait...{' '}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        </>
                      ) : (
                        <>Accept</>
                      )}
                    </button>
                  )}

                  {selectedFile && policyData[selectedFile?.id]?.completed_at ? (
                    <span className='p-1'>
                      Completed on:{' '}
                      {policyData[selectedFile?.id]?.completed_at
                        ? format(
                            new Date(policyData[selectedFile?.id]?.completed_at.seconds * 1000),
                            "MMMM dd, yyyy 'at' h:mm:ss a"
                          )
                        : ''}
                    </span>
                  ) : (
                    <span className='p-1 fs-6 text-gray-900'>
                    Please note that you are required to read all the contents in order
                    to accept the policy.
                  </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}

export default Policies
